import { computed, Ref, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { ContentFile, ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { Participant } from "@scrile/api-provider/dist/api/ParticipantProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { getVideoDuration } from "@/hooks/useAttachmentFilesInfo";
import emitter from "@/lib/emitter";
import { getNameMimeType, isVideoFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { AttachmentFile } from "@/types";
import { EVENTS } from "@/const";
import usePostData from "@/components/PostCreate/hooks/usePostData";
import usePostFiles from "@/components/PostCreate/hooks/usePostFiles";
import usePostParticipants from "@/components/PostCreate/hooks/usePostParticipants";
import { AddingFilesOptions, GenerateFileData } from "@/components/PostCreate/types";

export interface FullCreatorPendingFiles {
  files: FileList | File[] | AttachmentFile[];
  options: AddingFilesOptions;
  fromVault: boolean;
}

export interface FullCreatorVideoPostRetryState {
  description: string;
  localParticipants: Participant[];
  generateTrailer: boolean;
  trailerRange: Pick<GenerateFileData, "startTime" | "finishTime">;
  acceptedTrailerRequest: string;
  fileUploadFailed: boolean;
  mainVideoUploadFailed: boolean;
}

interface FullCreatorVideoPostControllerProps {
  postId: Ref<string>;
  userId: Ref<string>;
  pendingFiles: Ref<FullCreatorPendingFiles | null>;
  retryState?: FullCreatorVideoPostRetryState;
}

interface FullCreatorVideoPostControllerCallbacks {
  onFileUploaded: (postId: string, fileId: string) => void;
}

type FullCreatorVideoPostControllerEmit = (
  event: "expand" | "remove" | "pendingFilesConsumed" | "validChange",
  ...args: any[]
) => void;

export default function useFullCreatorVideoPostController(
  props: FullCreatorVideoPostControllerProps,
  emit: FullCreatorVideoPostControllerEmit,
  callbacks: FullCreatorVideoPostControllerCallbacks
) {
  const { postData, isDraft, getPostData, updatePost, deletePost, onPartialUpdate } = usePostData();

  const postTypeRef = computed<string>(() => postData.value?.type || ContentFeedTypeEnum.MEDIA);
  const deferredRef = computed(() => !isDraft.value);

  const {
    postFileDataList,
    generateFileData,
    errorUploadingFile,
    getPostFiles,
    uploadedFilesNumber,
    isUploadingFiles,
    waitFileUpload,
    updatePostFiles,
    onAddFiles: hookAddFiles,
    onAddFilesFromVault: hookAddFilesFromVault,
    onFileRemove,
    stopAndRemoveUploadingFiles,
    discardSettledUploadingFiles,
    addRequestGenerateFile,
    removeRequestGenerateFile,
  } = usePostFiles(props.postId, postTypeRef, deferredRef, {
    ignoreRemovedUploadSuccess: true,
    keepUploaderQueueOnError: true,
  });

  const {
    localParticipants,
    participantsCount,
    getParticipants,
    setParticipants,
    updateParticipant,
    syncParticipants,
  } = usePostParticipants(props.postId);

  const description = ref(props.retryState?.description ?? "");
  const hasParticipants = computed(() => participantsCount.value > 0);
  const generateTrailer = ref(props.retryState?.generateTrailer ?? false);
  const fileUploadFailed = ref(props.retryState?.fileUploadFailed ?? false);
  const mainVideoUploadFailed = ref(props.retryState?.mainVideoUploadFailed ?? false);
  const acceptedTrailerRequest = ref(props.retryState?.acceptedTrailerRequest ?? "");
  const mainVideoRemoving = ref(false);
  const dataLoaded = ref(false);
  const trailerRange = ref<Pick<GenerateFileData, "startTime" | "finishTime">>({
    startTime: props.retryState?.trailerRange.startTime ?? 0,
    finishTime: props.retryState?.trailerRange.finishTime ?? 120000,
  });

  const mainVideos = computed(() =>
    postFileDataList.value.filter(i => !i.public && isVideoFile(i) && !(mainVideoUploadFailed.value && i.uploading))
  );
  const pendingMainVideo = computed(() => {
    if (!props.pendingFiles.value || props.pendingFiles.value.options.publicAccess) return false;
    const [file] = Array.from(props.pendingFiles.value.files as ArrayLike<File | AttachmentFile>);
    const mediaType = props.pendingFiles.value.options.validatedMediaType ?? file?.type ?? "";
    return mediaType.startsWith("video");
  });
  const mainVideoLoading = computed(
    () => pendingMainVideo.value && !mainVideoUploadFailed.value && !mainVideos.value.length
  );
  const mainVideoMissing = computed(
    () =>
      dataLoaded.value &&
      !props.pendingFiles.value &&
      !mainVideoRemoving.value &&
      !mainVideos.value.length &&
      !mainVideoUploadFailed.value
  );
  const showMainVideoRecovery = computed(() => mainVideoUploadFailed.value || mainVideoMissing.value);
  const mainVideoRecoveryText = computed(() =>
    mainVideoUploadFailed.value
      ? $t("File upload failed. Please try again")
      : $t("Main video file is missing. Please upload it again")
  );
  const trailers = computed(() => postFileDataList.value.filter(i => i.public && isVideoFile(i)));
  const sfwThumbnails = computed(() =>
    postFileDataList.value.filter(i => i.thumbnailType === ContentFileThumbnailType.SFW)
  );
  const nsfwThumbnails = computed(() =>
    postFileDataList.value.filter(i => i.thumbnailType === ContentFileThumbnailType.NSFW)
  );

  const cardTitle = computed(() => mainVideos.value[0]?.fileName ?? "");

  const isPostEmpty = computed(() => mainVideos.value.every(v => v.uploading));

  const validationMap = computed(() => ({
    description: !!description.value,
    trailer: trailers.value.length > 0 || generateTrailer.value,
    sfwThumbnails: sfwThumbnails.value.length > 0,
    nsfwThumbnails: nsfwThumbnails.value.length > 0,
    participants: hasParticipants.value,
  }));

  const validationGoal = computed(() => Object.values(validationMap.value).length);
  const validationCurrent = computed(() => Object.values(validationMap.value).filter(Boolean).length);
  const isComplete = computed(() => validationCurrent.value === validationGoal.value && mainVideos.value.length > 0);

  const getRetryState = (): FullCreatorVideoPostRetryState => ({
    description: description.value,
    localParticipants: [...localParticipants.value],
    generateTrailer: generateTrailer.value,
    trailerRange: { ...trailerRange.value },
    acceptedTrailerRequest: acceptedTrailerRequest.value,
    fileUploadFailed: fileUploadFailed.value,
    mainVideoUploadFailed: mainVideoUploadFailed.value,
  });

  const onCollapseChange = (val: boolean) => {
    if (!val) {
      emit("expand");
    }
  };

  const onMainVideoRemove = async () => {
    mainVideoRemoving.value = true;
    try {
      await stopAndRemoveUploadingFiles();
      await deletePost();
      emit("remove", props.postId.value);
    } catch {
      // Keep the card visible so the user can retry or close it again.
    } finally {
      mainVideoRemoving.value = false;
    }
  };

  const onTrailerRangeChange = ({ startTime, finishTime }: Pick<GenerateFileData, "startTime" | "finishTime">) => {
    trailerRange.value = { startTime, finishTime };
  };

  const onTrailerRangeReset = () => {
    generateTrailer.value = false;
    trailerRange.value = { startTime: 0, finishTime: 120000 };
  };

  const emitNotification = (text: string) => {
    emitter.emit(EVENTS.NOTIFICATION_SHOW, { text });
  };

  const markFileUploadFailed = () => {
    fileUploadFailed.value = true;
  };

  const markMainVideoUploadFailed = () => {
    markFileUploadFailed();
    mainVideoUploadFailed.value = true;
  };

  const handleFileUploaded = (file?: ContentFile) => {
    if (file) callbacks.onFileUploaded(props.postId.value, file.id);
  };

  const getMainVideoUploadOptions = async (files: File[]) => {
    const [file] = files;
    if (!file) return null;
    const durations = await Promise.all(files.map(file => getVideoDuration(file)));
    if (durations.some(i => i < config.postMainVideoMinDurationMs / 1000)) {
      emitNotification($t("Videos have to be at least {0} minutes long", [config.postMainVideoMinDurationMs / 60000]));
      return null;
    }
    return {
      publicAccess: false,
      validatedMediaType: file.type.startsWith("video") ? file.type : getNameMimeType(file.name),
    };
  };

  const addMainVideoFiles = async (files: FileList | File[], options: AddingFilesOptions) => {
    fileUploadFailed.value = false;
    mainVideoUploadFailed.value = false;
    errorUploadingFile.value = false;
    await stopAndRemoveUploadingFiles();
    try {
      await hookAddFiles(files, options, handleFileUploaded, markMainVideoUploadFailed);
    } catch (e) {
      markMainVideoUploadFailed();
      throw e;
    }
  };

  const onMainVideoRetry = async (files: FileList) => {
    const fileList = Array.from(files);
    try {
      const options = await getMainVideoUploadOptions(fileList);
      if (!options) return;
      await addMainVideoFiles(fileList, options);
    } catch (e) {
      if ("message" in (e as Error)) emitNotification((e as Error).message);
    }
  };

  const processPendingFiles = async () => {
    if (!props.pendingFiles.value) return;
    const { files, options, fromVault } = props.pendingFiles.value;
    try {
      if (fromVault) {
        await hookAddFilesFromVault(files as AttachmentFile[], options);
      } else {
        await addMainVideoFiles(files as FileList | File[], options);
      }
    } catch {
      markMainVideoUploadFailed();
    } finally {
      emit("pendingFilesConsumed", props.postId.value);
    }
  };

  const loadData = async () => {
    await Promise.all([
      getPostData({ userId: props.userId.value, postId: props.postId.value }),
      getPostFiles(props.postId.value),
      getParticipants(),
    ]);
    dataLoaded.value = true;
    if (postData.value) {
      description.value = props.retryState?.description ?? postData.value.description ?? "";
      if (props.retryState) {
        setParticipants(props.retryState.localParticipants);
      }
      processPendingFiles();
    }

    emit("validChange", props.postId.value, isComplete.value);
  };

  const onAddFiles = async (files: FileList | File[], options: AddingFilesOptions) => {
    fileUploadFailed.value = false;
    errorUploadingFile.value = false;
    try {
      await hookAddFiles(files, options, handleFileUploaded, markFileUploadFailed);
    } catch (error) {
      markFileUploadFailed();
      throw error;
    }
  };

  const onAddFilesFromVault = async (files: AttachmentFile[], options?: AddingFilesOptions) => {
    fileUploadFailed.value = false;
    errorUploadingFile.value = false;
    try {
      await hookAddFilesFromVault(files, options);
    } catch (error) {
      markFileUploadFailed();
      throw error;
    }
  };

  const waitForUploads = async () => {
    await waitFileUpload();
    if (fileUploadFailed.value || errorUploadingFile.value || mainVideoUploadFailed.value) {
      throw new Error("Full Creator application video upload failed");
    }
  };

  const cleanupFailedUploads = () => {
    const staleFiles = discardSettledUploadingFiles();
    if (staleFiles.some(file => !file.public && isVideoFile(file))) {
      mainVideoUploadFailed.value = true;
    }
    fileUploadFailed.value = true;
  };

  const getTrailerRequestSignature = () => {
    if (!generateTrailer.value || !mainVideos.value[0]) return "";
    return [mainVideos.value[0].id, trailerRange.value.startTime, trailerRange.value.finishTime].join(":");
  };

  const publish = async () => {
    await waitForUploads();
    const trailerRequestSignature = getTrailerRequestSignature();
    if (generateTrailer.value) {
      trailers.value.forEach(i => onFileRemove(i.id));
      if (trailerRequestSignature !== acceptedTrailerRequest.value) {
        addRequestGenerateFile({
          id: mainVideos.value[0].id,
          publicAccess: true,
          startTime: trailerRange.value.startTime,
          finishTime: trailerRange.value.finishTime,
        } as GenerateFileData);
      } else {
        generateFileData.value.forEach(i => removeRequestGenerateFile(i.id));
      }
    } else {
      generateFileData.value.forEach(i => removeRequestGenerateFile(i.id));
    }
    await updatePostFiles();
    if (trailerRequestSignature) {
      acceptedTrailerRequest.value = trailerRequestSignature;
    }
    if (fileUploadFailed.value || errorUploadingFile.value || mainVideoUploadFailed.value || !mainVideos.value.length) {
      throw new Error("Full Creator application video upload failed");
    }
    const data = { description: description.value, published: true };
    // Files are persisted by updatePostFiles above, so the backend can resolve the post's main
    // (publicAccess=false) video when creating participants.
    await syncParticipants(data);
    await updatePost(data);
  };

  const remove = async () => {
    await stopAndRemoveUploadingFiles();
    await deletePost();
  };

  watch(mainVideos, (newVal, oldVal) => {
    if (newVal.length === 0 && oldVal.length > 0) {
      onTrailerRangeReset();
    }
  });

  watch(
    description,
    debounce((val: string) => {
      onPartialUpdate({ data: { description: val } });
    }, 500)
  );

  watch(isComplete, val => {
    emit("validChange", props.postId.value, val);
  });

  loadData();

  return {
    ContentFileThumbnailType,
    postData,
    isDraft,
    postFileDataList,
    errorUploadingFile,
    uploadedFilesNumber,
    isUploadingFiles,
    description,
    localParticipants,
    setParticipants,
    updateParticipant,
    generateTrailer,
    trailerRange,
    mainVideos,
    mainVideoLoading,
    showMainVideoRecovery,
    mainVideoRecoveryText,
    trailers,
    sfwThumbnails,
    nsfwThumbnails,
    cardTitle,
    isPostEmpty,
    validationGoal,
    validationCurrent,
    isComplete,
    onCollapseChange,
    onAddFiles,
    onAddFilesFromVault,
    onFileRemove,
    onMainVideoRemove,
    onMainVideoRetry,
    onTrailerRangeChange,
    onTrailerRangeReset,
    markFileUploadFailed,
    markMainVideoUploadFailed,
    waitForUploads,
    cleanupFailedUploads,
    getRetryState,
    publish,
    remove,
  };
}

export type FullCreatorVideoPostController = ReturnType<typeof useFullCreatorVideoPostController>;
